"use client";

import { useState, useEffect, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // On page load, scroll to hash if present (handles cross-page navigation)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to let the page render before scrolling
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [pathname]);

  const handleHashClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      const hash = href.replace("/", "");
      if (pathname === "/") {
        // Already on homepage — just smooth scroll
        e.preventDefault();
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        // On a different page — force full navigation so hash scroll works
        e.preventDefault();
        window.location.href = href;
      }
    },
    [pathname]
  );

  const links = [
    { label: "Solutions", href: "/#solutions" },
    { label: "Pricing", href: "/#pricing" },
    { label: "Blog", href: "/blog" },
  ];

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4 pt-4 pointer-events-none">
      <nav
        className={`pointer-events-auto flex items-center gap-8 px-4 py-2.5 rounded-full w-auto transition-all duration-500 ${
          scrolled
            ? "bg-surface/70 backdrop-blur-xl border border-surface-hover shadow-lg shadow-black/30"
            : "bg-transparent border border-transparent"
        }`}
      >
        <a href="/" className="flex items-center gap-2 shrink-0">
          <span className="text-lg font-bold tracking-tight text-content">
            Tethra
          </span>
          <span className="text-[9px] font-semibold uppercase tracking-widest text-coral-400 border border-coral-500/30 bg-coral-500/10 rounded-full px-2 py-0.5">
            AI
          </span>
        </a>

        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={
                l.href.startsWith("/#")
                  ? (e) => handleHashClick(e, l.href)
                  : undefined
              }
              className="relative text-sm text-content-secondary hover:text-content transition-colors duration-200 whitespace-nowrap after:content-[''] after:absolute after:-bottom-0.5 after:left-0 after:w-full after:h-px after:bg-current after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100"
            >
              {l.label}
            </a>
          ))}
          <span className="w-px h-4 bg-surface-border" />
          <a
            href="/login"
            className="text-sm text-content-secondary hover:text-content transition-colors whitespace-nowrap"
          >
            Sign in
          </a>
          <a
            href="/signup"
            className="text-sm font-medium bg-coral-500 hover:bg-coral-400 text-white rounded-full px-5 py-2 transition-all duration-200 active:scale-95 shadow-lg shadow-coral-500/20 hover:shadow-coral-500/40 whitespace-nowrap"
          >
            Start Free Trial
          </a>
        </div>

        <button
          className="md:hidden text-content-secondary hover:text-content cursor-pointer ml-auto"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="pointer-events-auto absolute top-[68px] left-4 right-4 bg-surface/95 backdrop-blur-xl border border-surface-border rounded-2xl px-6 py-5 space-y-3 shadow-xl shadow-black/40"
        >
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="block text-sm text-content-secondary hover:text-content py-2"
              onClick={(e) => {
                setOpen(false);
                if (l.href.startsWith("/#")) handleHashClick(e, l.href);
              }}
            >
              {l.label}
            </a>
          ))}
          <div className="flex flex-col gap-3 pt-3 border-t border-surface-border">
            <a
              href="/login"
              className="text-sm text-content-secondary hover:text-content"
            >
              Sign in
            </a>
            <a
              href="/signup"
              className="text-sm font-medium bg-coral-500 text-white rounded-full px-5 py-2.5 w-fit active:scale-95 cursor-pointer text-center"
            >
              Start Free Trial
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
}
